import { useEffect } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { Navigate, Outlet, useSearchParams } from 'react-router-dom';

import { MODULES } from 'common/constants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import storageWrapper from 'common/loginUtils/storage-wrapper';
import { RootState } from 'common/store/AppStore';
import { setReturnTo } from 'common/store/reducer/commonReducer';
import { isModuleExists } from 'common/utils/authCheck';
import { urlWithTenant } from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

interface Props {
	allowedModules: string[];
}

const {
	HOME,
	RISK_CHANGE,
	REGULATION_CHANGE,
	DATA_INGESTION,
	TESTING_AND_MONITORING,
	AI_MODELS,
	RISK_REFERENCE_TAXONOMY,
	REQUIREMENT_INVENTORY,
	AI_SIMULATION_AND_EXPERIMENTATION,
	REPORTING_DATABASE,
	HORIZON_SCAN_AND_ALERTS,
	MANAGEMENT,
} = MODULES;

const PrivateRoute = ({ allowedModules }: Props) => {
	const dispatch = useDispatch();
	const [searchParams] = useSearchParams();
	const isLoggedIn = useSelector(
		(state: RootState) => state.common.isAuthenticated,
	);

	useEffect(() => {
		if (searchParams && searchParams.get('redirectTo')) {
			const params = searchParams.get('redirectTo');
			const decodedUri = decodeURIComponent(params ?? '');
			storageWrapper.setStorageItem('returnTo', decodedUri);
			dispatch(setReturnTo(decodedUri));
		} else {
			dispatch(setReturnTo(null));
		}
	}, [searchParams]);

	const aiSimulationEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_AI_SIMULATION_AND_EXPERIMENTATION_ENABLED,
	);
	const testingAndMonitoringEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_TESTING_AND_MONITORING_ENABLED,
	);
	const requirementInventoryEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_REGULATION_INVENTORY_ENABLED,
	);
	const riskReferenceTaxonomyEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_RISK_REFERENCE_TAXONOMY_ENABLED,
	);
	const horizonScanEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_HORIZON_SCANNING_ENABLED,
	);
	const managementEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_MANAGEMENT_ENABLED,
	);
	const dataIngestionEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_DATA_INGESTION_ENABLED,
	);
	const aiModelsEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_AI_MODELS_ENABLED,
	);
	const reportingDatabaseEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_REPORTING_LIBRARY_ENABLED,
	);
	const riskChangeEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_RISK_CHANGE_ENABLED,
	);
	const regulationChangeEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_REGULATION_CHANGE_ENABLED,
	);

	const getFeatureFlags = () => {
		switch (allowedModules[0]) {
			case REQUIREMENT_INVENTORY:
				return requirementInventoryEnabled;
			case RISK_REFERENCE_TAXONOMY:
				return riskReferenceTaxonomyEnabled;
			case HORIZON_SCAN_AND_ALERTS:
				return horizonScanEnabled;
			case AI_SIMULATION_AND_EXPERIMENTATION:
				return aiSimulationEnabled;
			case RISK_CHANGE:
				return riskChangeEnabled;
			case REGULATION_CHANGE:
				return regulationChangeEnabled;
			case DATA_INGESTION:
				return dataIngestionEnabled;
			case TESTING_AND_MONITORING:
				return testingAndMonitoringEnabled;
			case AI_MODELS:
				return aiModelsEnabled;
			case REPORTING_DATABASE:
				return reportingDatabaseEnabled;
			case MANAGEMENT:
				return managementEnabled;
			case HOME:
				return true;
			default:
				return false;
		}
	};

	if (!isLoggedIn) {
		return <Navigate to="/" replace />;
	}

	if (!isModuleExists(allowedModules) || !getFeatureFlags()) {
		return <Navigate to={urlWithTenant('/unauthorized')} />;
	}

	return <Outlet />;
};

export default PrivateRoute;
